import { useSyncExternalStore } from "react";

export interface Store<T> {
  get(): T;
  set(next: T | ((prev: T) => T)): void;
  subscribe(fn: () => void): () => void;
}

export function createStore<T>(initial: T, opts?: { persist?: string; version?: number }): Store<T> {
  let value = initial;
  if (opts?.persist) {
    try {
      const raw = localStorage.getItem(opts.persist);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.v === (opts.version ?? 1)) value = { ...initial, ...parsed.d };
      }
    } catch {
      /* ignore broken storage */
    }
  }
  const subs = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      const v = typeof next === "function" ? (next as (p: T) => T)(value) : next;
      if (Object.is(v, value)) return;
      value = v;
      if (opts?.persist) {
        try {
          localStorage.setItem(opts.persist, JSON.stringify({ v: opts.version ?? 1, d: value }));
        } catch {
          /* quota / private mode */
        }
      }
      subs.forEach((f) => f());
    },
    subscribe(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}

export function useStore<T>(s: Store<T>): T {
  return useSyncExternalStore(s.subscribe, s.get, s.get);
}

/** Safe localStorage helpers (private mode / blocked storage never throw). */
export const storage = {
  get<T>(key: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
      return fallback;
    }
  },
  set(key: string, value: unknown) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  },
  del(key: string) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};
